import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Plus, Trash2, FileText, ChevronRight, ChevronDown, Printer } from 'lucide-react';
import { Button, Chip } from '@/ui/primitives';
import {
  derivarStatusOnus,
  novoOnusItem,
  ONUS_STATUS,
  ONUS_STATUS_LABEL,
  parseOnusItems,
  type OnusItem,
  type OnusStatus,
} from '../onus';

interface Props {
  raw: unknown;
  legacyText?: string | null;
  onChange: (items: OnusItem[]) => Promise<void> | void;
  context?: OnusBaixaContext | undefined;
}

export interface OnusBaixaContext {
  nomeDono?: string | null | undefined;
  cpfDono?: string | null | undefined;
  matricula?: string | number | null | undefined;
  cartorio?: string | number | null | undefined;
  municipio?: string | number | null | undefined;
  uf?: string | number | null | undefined;
  imovelNome?: string | number | null | undefined;
  lote?: string | number | null | undefined;
  gleba?: string | number | null | undefined;
  proprietarioNome?: string | number | null | undefined;
}

// Logo Ruralplan (SVG cropado pra eliminar whitespace, base64) — usado no header do PDF de baixa de ônus.
const LOGO_RURALPLAN_B64 =
  'PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2ZXJzaW9uPSIxLjEiIHZpZXdCb3g9IjUxMCA0NzggOTAwIDEyNCI+CiAgPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDMwLjMuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDIuMS4zIEJ1aWxkIDE4MikgIC0tPgogIDxkZWZzPgogICAgPHN0eWxlPgogICAgICAuc3QwIHsKICAgICAgICBmaWxsOiAjMDAyOTI4OwogICAgICB9CgogICAgICAuc3QxIHsKICAgICAgICBmaWxsOiAjOWM0YTIzOwogICAgICB9CiAgICA8L3N0eWxlPgogIDwvZGVmcz4KICA8cGF0aCBjbGFzcz0ic3QxIiBkPSJNMTIwMi41NCw1NjQuMTRjLTUuMDcsMTEuMzctMTIuMDIsMjkuMy0xMi4zOCwyOS44MS0uMzIuNDYtLjcyLjg3LTEuMTksMS4yM3Q5LjIyLDBjMTAuNDMsMCwxOC4xMS02LjYsMjEuMTUtMTUuNTgsMi45Ny04Ljc0LDEwLjIzLTI4LjI4LDE1LjUzLTMwLjg0LTQuNC0uMDItNC4yLS4xMi05LjUtLjEzLTEwLjc1LjAyLTE4Ljg0LDYuNTMtMjIuODQsMTUuNTJaIi8+CiAgPHBvbHlnb24gY2xhc3M9InN0MSIgcG9pbnRzPSIxMjI2LjMgNTA5LjgxIDEyNjMuMDQgNTk1LjA5IDEyODUuNDYgNTk1LjA5IDEyMzcuMjIgNDg0LjgyIDEyMjYuMyA1MDkuODEiLz4KICA8cGF0aCBjbGFzcz0ic3QxIiBkPSJNMTA4NC41MSw0ODkuNjFjLTUuMDUtMi43My0xMi40Ny00LjMzLTIyLjEtNC43Nyw1LjUyLDMuMjQsOS45LDEwLjI1LDkuOSwyNC4zMiwwLDExLjU3LTMuMDIsMTkuNzktOS4wMSwyNC40Ni0xLjg2LDEuNC0zLjk1LDIuNS02LjI0LDMuMjYsMTIuMDMtLjE0LDIxLjc5LTIuODUsMjkuMDItOC4wNSw3LjI4LTUuMjUsMTAuODMtMTEuOTgsMTAuODMtMjAuNTYsMC04LTQuMDYtMTQuMTEtMTIuMzktMTguNjZaIi8+CiAgPHJlY3QgY2xhc3M9InN0MSIgeD0iMTAxMy45IiB5PSI0ODQuODQiIHdpZHRoPSIyMi4zNiIgaGVpZ2h0PSIxMTAuMjMiLz4KICA8cGF0aCBjbGFzcz0ic3QxIiBkPSJNMTE4Miw1NzUuMTFjLTQuNDItLjAzLTEzLjIxLS4xNi0yMi4xNi0uMjQtOS43Ny0uMDktMTguNiw0LTIyLjIzLDEyLjYtMS43Niw0LjIzLTEuNjEsNC4xMS0zLjA2LDcuNjIsMy4yNC0yLjQ1LDE4Ljg5LTEuODgsMjguNjktMS44OCwxMS40MiwwLDE2LjYyLTIuOTIsMjAuMTMtMTEuMjVxMy4xLTcuMzcsMy4xMS03LjM3cy0uMDQuMDEtLjA2LjAyYy0uMjYuMS0xLjQ5LjUtNC40MS41WiIvPgogIDxyZWN0IGNsYXNzPSJzdDEiIHg9IjExMDQuMDgiIHk9IjQ4NC44MiIgd2lkdGg9IjIyLjQxIiBoZWlnaHQ9IjExMC4yNyIvPgogIDxwYXRoIGNsYXNzPSJzdDAiIGQ9Ik04MzQuOCw1NjQuMTRjLTUuMDcsMTEuMzctMTIuMDIsMjkuMy0xMi4zOCwyOS44MS0uMzIuNDYtLjcyLjg3LTEuMTksMS4yM3Q5LjIyLDBjMTAuNDMsMCwxOC4xMS02LjYsMjEuMTUtMTUuNTgsMi45Ny04Ljc0LDEwLjIzLTI4LjI4LDE1LjUzLTMwLjg0LTQuNC0uMDItNC4yLS4xMi05LjUtLjEzLTEwLjc1LjAyLTE4Ljg0LDYuNTMtMjIuODQsMTUuNTJaIi8+CiAgPHBhdGggY2xhc3M9InN0MCIgZD0iTTEwMDYuOTUsNTc0LjYyYy0uMjYuMS0xLjQ5LjUtNC40MS41LTQuNDItLjAzLTEzLjIxLS4xNi0yMi4xNi0uMjQtOS43Ny0uMDktMTguNiw0LTIyLjIzLDEyLjYtMS43Niw0LjIzLTEuNjEsNC4xMS0zLjA2LDcuNjIsMy4yNC0yLjQ1LDE4Ljg5LTEuODgsMjguNjktMS44OCwxMS40MiwwLDE2LjYyLTIuOTIsMjAuMTMtMTEuMjVxMy4xLTcuMzcsMy4xMS03LjM3cy0uMDQuMDEtLjA2LjAyWiIvPgogIDxwYXRoIGNsYXNzPSJzdDAiIGQ9Ik01ODAuNzYsNTA2LjAyYzAsMTAuMzItMi40NCwxOC4wMS03LjI1LDIyLjgzLS44Ny44Ny0xLjgyLDEuNjYtMi44NywyLjM3LDcuMDctMSwxMi42NS0yLjMsMTYuNjQtMy45LDExLjQxLTQuNjEsMTYuOTUtMTEuNDIsMTYuOTUtMjAuODIsMC00LjA0LTEuMjktNy42NS0zLjkzLTExLjAzLTIuNjYtMy40LTYuMjctNS45Ny0xMC43NC03LjY0LTMuOTQtMS40OS0xMC4zMi0yLjUtMTkuMDUtMy4wMSw1Ljc2LDIuOTYsMTAuMjQsOS4xMywxMC4yNCwyMS4yWiIvPgogIDxwYXRoIGNsYXNzPSJzdDAiIGQ9Ik01OTQuNjEsNTUzLjAxYy02LjExLTEwLjUtMTYuMzQtMTcuMTgtMjYuOS0xNy4yMS01LjY3LjAyLTguMjguMTItMTIuNjEuMTQsNy4yLDMuMjEsMjUuMTgsMzMuMjUsMjkuOTUsNDIuNzgsNC44OCw5Ljc1LDE3LDE2LjM3LDI3LjI1LDE2LjM3cTkuMDcsMCw5LjA3LDBjLS41Ny0uMzgtMS4wOC0uODEtMS41My0xLjI5LS41LS41My0xNC4xMS0yMS42Ny0yNS4yMy00MC43OVoiLz4KICA8cmVjdCBjbGFzcz0ic3QwIiB4PSI1MjUiIHk9IjQ4NC44MiIgd2lkdGg9IjIyLjkyIiBoZWlnaHQ9IjExMC4yNyIvPgogIDxwYXRoIGNsYXNzPSJzdDAiIGQ9Ik03NzMuNzMsNTA2LjAyYzAsMTAuMzItMi40NCwxOC4wMS03LjI1LDIyLjgzLS44Ny44Ny0xLjgyLDEuNjYtMi44NywyLjM3LDcuMDctMSwxMi42NS0yLjMsMTYuNjQtMy45LDExLjQxLTQuNjEsMTYuOTUtMTEuNDIsMTYuOTUtMjAuODIsMC00LjA0LTEuMjktNy42NS0zLjkzLTExLjAzLTIuNjYtMy40LTYuMjYtNS45Ny0xMC43NC03LjY0LTMuOTQtMS40OS0xMC4zMi0yLjUtMTkuMDUtMy4wMSw1Ljc2LDIuOTYsMTAuMjQsOS4xMywxMC4yNCwyMS4yWiIvPgogIDxwYXRoIGNsYXNzPSJzdDAiIGQ9Ik03ODcuNTgsNTUzLjAxYy02LjExLTEwLjUtMTYuMzQtMTcuMTgtMjYuOS0xNy4yMS01LjY3LjAyLTguMjguMTItMTIuNjEuMTQsNy4yLDMuMjEsMjUuMTgsMzMuMjUsMjkuOTUsNDIuNzgsNC44OCw5Ljc1LDE3LDE2LjM3LDI3LjI1LDE2LjM3cTkuMDcsMCw5LjA3LDBjLS41Ny0uMzgtMS4wOC0uODEtMS41My0xLjI5LS41LS41My0xNC4xMS0yMS42Ny0yNS4yMy00MC43OVoiLz4KICA8cmVjdCBjbGFzcz0ic3QwIiB4PSI3MTcuOTciIHk9IjQ4NC44MiIgd2lkdGg9IjIyLjkyIiBoZWlnaHQ9IjExMC4yNyIvPgogIDxwb2x5Z29uIGNsYXNzPSJzdDAiIHBvaW50cz0iODU4LjU1IDUwOS44MSA4OTUuMyA1OTUuMDkgOTE3LjcyIDU5NS4wOSA4NjkuNDcgNDg0LjgyIDg1OC41NSA1MDkuODEiLz4KICA8cmVjdCBjbGFzcz0ic3QwIiB4PSI5MjQuNjEiIHk9IjQ4NC44MiIgd2lkdGg9IjIyLjQxIiBoZWlnaHQ9IjExMC4yNyIvPgogIDxwYXRoIGNsYXNzPSJzdDAiIGQ9Ik02NDYuOTgsNTgxLjgxYy4zMy0yOS4zNSwwLTk2Ljk5LDAtOTYuOTloLTIyLjY0djkwLjE4YzAsMTEuMDksOC45OSwyMC4wOSwyMC4wOSwyMC4wOWgxMi43N2MtNS42NCwwLTEwLjIyLTcuNjQtMTAuMjItMTMuMjhaIi8+CiAgPHJlY3QgY2xhc3M9InN0MCIgeD0iNjgzLjQ3IiB5PSI0ODQuODIiIHdpZHRoPSIyMi43NyIgaGVpZ2h0PSIxMTAuMjciLz4KICA8cGF0aCBjbGFzcz0ic3QxIiBkPSJNMTI5Ny4xMSw1NTUuMDVjMCw4LjkxLS4xMSwyOC41OC0uMzMsMzUuODMtLjA4LDMuMDUtMS4zNCw0LjIxLTEuMzQsNC4yMWgzLjMyYzEuNzcsMCw0LjM2LDAsOC4xMSwwLDYuMjQsMCwxMy4zNi01Ljk3LDEzLjM2LTE1LjE1cy0xLjUzLTM3LjM1LDMuNjMtMzkuODVjLTQuMjgtLjAyLTQuMDgtLjEyLTkuMjUtLjEzLTEwLjQ1LjAyLTE3LjQ5LDUuNTktMTcuNDksMTUuMVoiLz4KICA8cGF0aCBjbGFzcz0ic3QxIiBkPSJNMTM5My41LDU4Mi42di04NS4wNmMuMDQtMy4xMi4wOS01Ljg4LjE0LTcuOTEuMTUtMy4yMy45Mi00LjM2LDEuMjMtNC42OS4wNS0uMDQuMDgtLjA4LjEzLS4xMWgtMS41czAsMCwwLDBoLTguNzZjLTguMDIsMC0xNC41Miw2LjUtMTQuNTIsMTQuNTJ2NjAuNjlsLTQ5LjkyLTc1LjIxaC0yMy4zbDY2LjM1LDk5LjExYzQuMzEsNi40MywxMS4zMSwxMC40OSwxOC45NiwxMS4wOC4wOCwwLC4xNi4wMS4yNC4wMi4xMSwwLC4yMS4wMS4zMi4wMi4yMy4wMi40Ni4wNC42OS4wNGguNjJzLjA1LDAsLjA4LDBoOS4yM3MxLjQ2LDAsMS40NiwwaC4wNHMtMS4yNy0xLjE2LTEuMzQtNC4yMWMtLjA2LTEuOTgtLjExLTQuOTEtLjE2LTguMjhaIi8+Cjwvc3ZnPg==';

const STATUS_TONE: Record<OnusStatus, 'ok' | 'warn' | 'neutral'> = {
  vigente: 'ok',
  vencido_sem_baixa: 'warn',
  baixado: 'neutral',
};

export function OnusGravamesEditor({ raw, legacyText, onChange, context }: Props) {
  const items = useMemo(() => parseOnusItems(raw), [raw]);
  const [pendingNew, setPendingNew] = useState<OnusItem | null>(null);

  function emitirBaixa(item: OnusItem) {
    emitirRequerimentoPdf({ item, allItems: items, context });
  }

  async function persist(next: OnusItem[]) {
    await onChange(next);
  }

  async function commitField<K extends keyof OnusItem>(
    itemId: string,
    field: K,
    value: OnusItem[K],
  ) {
    const target = items.find((i) => i.id === itemId);
    if (!target) return;
    const updated: OnusItem = {
      ...target,
      [field]: value,
      updated_at: new Date().toISOString(),
    };
    if (field === 'status') {
      updated.status_source = 'manual';
    } else if (field === 'vencimento' && target.status_source === 'auto') {
      updated.status = derivarStatusOnus(value as string | null);
    }
    await persist(items.map((i) => (i.id === itemId ? updated : i)));
  }

  async function removeItem(id: string) {
    if (!confirm('Remover este gravame?')) return;
    await persist(items.filter((i) => i.id !== id));
  }

  function addItem(initialStatus: OnusStatus) {
    setPendingNew(novoOnusItem({ status: initialStatus, status_source: 'manual' }));
  }

  async function savePending() {
    if (!pendingNew) return;
    if (!pendingNew.credor.trim()) {
      alert('Informe o credor antes de salvar.');
      return;
    }
    await persist([...items, pendingNew]);
    setPendingNew(null);
  }

  const vigentes = items.filter((i) => i.status === 'vigente');
  const vencidos = items.filter((i) => i.status === 'vencido_sem_baixa');
  const baixados = items.filter((i) => i.status === 'baixado');

  const showLegacyAlone =
    legacyText && legacyText.trim().length > 0 && items.every((i) => !i.migrated_from_text);

  return (
    <div className="onus-editor flex flex-col gap-3 mt-2">
      <SectionHeader
        title="Gravames vigentes"
        count={vigentes.length}
        tone="ok"
        onAdd={() => addItem('vigente')}
      />
      <ItemList
        items={vigentes}
        onCommitField={commitField}
        onRemove={removeItem}
        emptyHint="Nenhum gravame vigente."
        allItems={items}
        context={context}
        onOpenBaixa={emitirBaixa}
      />

      <SectionHeader
        title="Vencidos / passíveis de baixa sem baixa formal"
        count={vencidos.length}
        tone="warn"
        onAdd={() => addItem('vencido_sem_baixa')}
      />
      <ItemList
        items={vencidos}
        onCommitField={commitField}
        onRemove={removeItem}
        emptyHint="Nenhum gravame vencido."
        allItems={items}
        context={context}
        onOpenBaixa={emitirBaixa}
      />

      {baixados.length > 0 && (
        <details className="rounded-lg border border-[var(--border)] bg-elevated px-3 py-2">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-[var(--text2)]">
            Baixados ({baixados.length})
          </summary>
          <div className="pt-2">
            <ItemList
              items={baixados}
              onCommitField={commitField}
              onRemove={removeItem}
              emptyHint=""
              allItems={items}
              context={context}
              onOpenBaixa={emitirBaixa}
            />
          </div>
        </details>
      )}

      {pendingNew && (
        <NewItemForm
          item={pendingNew}
          onChange={setPendingNew}
          onSave={savePending}
          onCancel={() => setPendingNew(null)}
        />
      )}

      {showLegacyAlone && (
        <div className="rounded-lg border border-dashed border-[var(--border)] bg-elevated p-3 text-xs">
          <div className="flex items-center gap-1.5 font-semibold text-[var(--text2)] mb-1">
            <FileText size={12} strokeWidth={1.5} />
            Texto legado (não estruturado)
          </div>
          <pre className="whitespace-pre-wrap font-sans text-[var(--text2)]">{legacyText}</pre>
          <div className="mt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setPendingNew(
                  novoOnusItem({
                    status: 'vigente',
                    status_source: 'manual',
                    observacoes: legacyText ?? '',
                    migrated_from_text: true,
                  }),
                )
              }
            >
              <Plus size={12} strokeWidth={1.5} />
              Estruturar como gravame
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({
  title,
  count,
  tone,
  onAdd,
}: {
  title: string;
  count: number;
  tone: 'ok' | 'warn';
  onAdd: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--text2)]">
          {title}
        </span>
        <Chip tone={tone} size="sm">
          {count}
        </Chip>
      </div>
      <Button variant="ghost" size="sm" onClick={onAdd}>
        <Plus size={12} strokeWidth={1.5} />
        Adicionar
      </Button>
    </div>
  );
}

function ItemList({
  items,
  onCommitField,
  onRemove,
  emptyHint,
  allItems,
  onOpenBaixa,
}: {
  items: OnusItem[];
  onCommitField: <K extends keyof OnusItem>(
    id: string,
    field: K,
    value: OnusItem[K],
  ) => Promise<void>;
  onRemove: (id: string) => void;
  emptyHint: string;
  allItems: OnusItem[];
  context?: OnusBaixaContext | undefined;
  onOpenBaixa: (item: OnusItem) => void;
}) {
  if (items.length === 0) {
    if (!emptyHint) return null;
    return <div className="text-xs text-[var(--muted)] italic px-1">{emptyHint}</div>;
  }
  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => (
        <ItemRow
          key={item.id}
          item={item}
          onCommitField={onCommitField}
          onRemove={() => onRemove(item.id)}
          allItems={allItems}
          onOpenBaixa={() => onOpenBaixa(item)}
        />
      ))}
    </div>
  );
}

function ItemRow({
  item,
  onCommitField,
  onRemove,
  allItems,
  onOpenBaixa,
}: {
  item: OnusItem;
  onCommitField: <K extends keyof OnusItem>(
    id: string,
    field: K,
    value: OnusItem[K],
  ) => Promise<void>;
  onRemove: () => void;
  allItems: OnusItem[];
  onOpenBaixa: () => void;
}) {
  const [open, setOpen] = useState(false);
  const tone = STATUS_TONE[item.status];
  const Chevron = open ? ChevronDown : ChevronRight;
  const credorGrupo = allItems.filter(
    (i) => sameCredor(i.credor, item.credor) && i.status !== 'baixado',
  ).length;

  return (
    <div className="rounded-lg border border-[var(--border)] bg-elevated">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 text-left cursor-pointer"
        aria-expanded={open}
      >
        <Chevron size={14} strokeWidth={1.5} className="text-[var(--muted)] shrink-0" />
        <span className="font-semibold text-[var(--text)] flex-1 min-w-0 break-words">
          {item.credor?.trim() ? (
            item.credor
          ) : (
            <em className="text-[var(--muted)] not-italic">— credor —</em>
          )}
        </span>
        <Chip tone={tone} size="sm">
          {ONUS_STATUS_LABEL[item.status]}
        </Chip>
        {item.vencimento && (
          <Chip tone={item.status === 'vencido_sem_baixa' ? 'danger' : 'neutral'} size="sm">
            {formatBR(item.vencimento)}
          </Chip>
        )}
        {credorGrupo > 1 && (
          <Chip tone="neutral" size="sm">
            {credorGrupo} atos/gravames
          </Chip>
        )}
        <span className="text-[0.7rem] text-[var(--muted)] shrink-0">
          {open ? 'Ocultar' : 'Ver mais'}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-5 pt-3 border-t border-[var(--border)] flex flex-col gap-4 text-xs">
          <DetailRow label="Credor">
            <InlineEdit
              value={item.credor}
              placeholder="Ex.: BASA, Banco do Brasil"
              onCommit={(v) => onCommitField(item.id, 'credor', v ?? '')}
            />
          </DetailRow>
          <DetailRow label="Tipo">
            <InlineEdit
              value={item.tipo}
              placeholder="Ex.: Hipoteca, Alienação fiduciária"
              onCommit={(v) => onCommitField(item.id, 'tipo', v ?? '')}
            />
          </DetailRow>
          <DetailRow label="Instrumento">
            <InlineEdit
              value={item.instrumento}
              placeholder="Ex.: Cédula 12/2024"
              onCommit={(v) => onCommitField(item.id, 'instrumento', v ?? '')}
            />
          </DetailRow>
          <DetailRow label="Ato registral">
            <InlineEdit
              value={item.ato_registral}
              placeholder="Ex.: AV-2-16.708 / R-21-2.906"
              onCommit={(v) => onCommitField(item.id, 'ato_registral', v ?? '')}
            />
          </DetailRow>
          <DetailRow label="Data do registro">
            <InlineEdit
              value={item.data_registro ?? ''}
              type="date"
              onCommit={(v) => onCommitField(item.id, 'data_registro', v || null)}
            />
          </DetailRow>
          <DetailRow label="Vencimento">
            <InlineEdit
              value={item.vencimento ?? ''}
              type="date"
              onCommit={(v) => onCommitField(item.id, 'vencimento', v || null)}
            />
          </DetailRow>
          <DetailRow label="Status">
            <InlineEdit
              value={item.status}
              type="select"
              options={ONUS_STATUS.map((s) => ({ value: s, label: ONUS_STATUS_LABEL[s] }))}
              onCommit={(v) => onCommitField(item.id, 'status', (v ?? 'vigente') as OnusStatus)}
              displayHint={
                item.status_source === 'auto' ? 'derivado do vencimento' : 'definido manualmente'
              }
            />
          </DetailRow>
          <DetailRow label="Observações">
            <InlineEdit
              value={item.observacoes}
              multiline
              placeholder="Detalhes adicionais"
              onCommit={(v) => onCommitField(item.id, 'observacoes', v ?? '')}
            />
          </DetailRow>

          <div className="flex justify-end items-center gap-2 pt-4 mt-2">
            <button
              type="button"
              onClick={onRemove}
              className="text-[var(--muted)] hover:text-[var(--danger)] transition-colors text-xs inline-flex items-center gap-1 px-2 py-1 rounded"
              title="Remover"
            >
              <Trash2 size={12} strokeWidth={1.5} />
              Remover
            </button>
            <Button
              variant="primary"
              size="sm"
              onClick={onOpenBaixa}
              title={
                item.credor?.trim()
                  ? 'Gerar solicitação de baixa'
                  : 'O pedido será gerado com o campo do credor para preenchimento'
              }
            >
              <Printer size={14} strokeWidth={1.5} />
              Emitir solicitação de baixa
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function emitirRequerimentoPdf({
  item,
  allItems,
  context,
}: {
  item: OnusItem;
  allItems: OnusItem[];
  context?: OnusBaixaContext | undefined;
}) {
  const atos = selecionarAtosParaBaixa(item, allItems);
  emitirSolicitacaoBaixaPdf({ atos, context });
}

export function emitirSolicitacaoBaixaPdf({
  atos,
  context,
}: {
  atos: OnusItem[];
  context?: OnusBaixaContext | undefined;
}) {
  const credor = Array.from(
    new Set(atos.map((ato) => clean(ato.credor)).filter(Boolean)),
  ).join(' / ');
  const html = montarHtmlRequerimentoPdf({
    credor,
    atos,
    context,
    email: '',
    telefone: '',
    setor: '',
  });
  const win = window.open('', '_blank');
  if (!win) {
    alert('Não foi possível abrir a janela de impressão. Verifique se o navegador está bloqueando popups.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  const trigger = () => {
    win.focus();
    win.print();
  };
  if (win.document.readyState === 'complete') setTimeout(trigger, 200);
  else win.addEventListener('load', () => setTimeout(trigger, 200));
}

export function selecionarAtosParaBaixa(item: OnusItem, allItems: OnusItem[]): OnusItem[] {
  const credor = normalizeCredor(item.credor);

  // Sem credor identificado, não agrupa atos distintos apenas porque o campo está vazio.
  if (!credor) return [item];

  // Atos do mesmo credor que ainda não foram baixados — incluem o item clicado.
  return allItems.filter(
    (i) =>
      normalizeCredor(i.credor) === credor &&
      i.status !== 'baixado' &&
      (i.status === 'vencido_sem_baixa' || i.id === item.id),
  );
}

function montarHtmlRequerimentoPdf({
  credor,
  atos,
  context,
  email,
  telefone,
  setor,
}: {
  credor: string;
  atos: OnusItem[];
  context?: OnusBaixaContext | undefined;
  email: string;
  telefone: string;
  setor: string;
}): string {
  const nome =
    clean(context?.nomeDono) || clean(context?.proprietarioNome) || '[NOME COMPLETO DO REQUERENTE]';
  const cpf = clean(context?.cpfDono) || '[XXX.XXX.XXX-XX]';
  const mat = clean(context?.matricula) || '[NÚMERO DA MATRÍCULA]';
  const cartorio =
    clean(context?.cartorio) ||
    `Registro de Imóveis da Comarca de ${clean(context?.municipio) || '[município]'}/${clean(context?.uf) || '[UF]'}`;
  const municipio = clean(context?.municipio) || '[Município]';
  const uf = clean(context?.uf) || '[UF]';
  const cidadeUf = `${municipio}/${uf}`;
  const dataExtenso = formatDataPtBR(new Date());
  const credorTxt = credor || '[RAZÃO SOCIAL DA EMPRESA CREDORA]';
  const setorLine = setor ? `<div class="d-sub" style="margin-top:1mm">${escapeHtml(setor)}</div>` : '';
  const contatoLinha =
    email || telefone
      ? `Para retorno do documento, preferencialmente em meio digital${
          email ? ` no e-mail <strong>${escapeHtml(email)}</strong>` : ''
        }${telefone ? `${email ? ' ou pelo' : ' pelo'} telefone <strong>${escapeHtml(telefone)}</strong>` : ''}.`
      : '';

  const cards = atos
    .map((a, idx) => {
      const tipo = clean(a.tipo) || 'Ônus / gravame';
      const atoRegistral =
        clean(a.ato_registral) || clean(resumoAtoRegistral(a)) || '—';
      const instrumento = clean(a.instrumento) || '—';
      const dataReg = a.data_registro || a.atos?.find((x) => clean(x.data))?.data;
      const dataRegFmt = dataReg ? formatBR(String(dataReg)) : '—';
      const venc = a.vencimento ? formatBR(a.vencimento) : '—';
      return `
    <div class="onus-card">
      <div class="onus-card-head">
        <span class="act-num">Ato ${idx + 1}</span>
        <span class="act-title">${escapeHtml(tipo)}</span>
      </div>
      <div class="onus-card-body">
        <div class="onus-row"><div class="k">Ato registral</div><div class="v mono">${escapeHtml(atoRegistral)}</div></div>
        <div class="onus-row"><div class="k">Instrumento</div><div class="v">${escapeHtml(instrumento)}</div></div>
        <div class="onus-row"><div class="k">Data do registro</div><div class="v">${escapeHtml(dataRegFmt)}</div></div>
        <div class="onus-row"><div class="k">Vencimento informado</div><div class="v">${escapeHtml(venc)}</div></div>
      </div>
    </div>`;
    })
    .join('');

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>Requerimento de Baixa de Ônus Real — ${escapeHtml(nome)}</title>
<style>
  :root{--font-sans:-apple-system,BlinkMacSystemFont,"SF Pro Text","Inter","Helvetica Neue",Arial,sans-serif;--font-mono:"SF Mono",ui-monospace,"JetBrains Mono",Menlo,Consolas,monospace;--ink:#1a1a1a;--ink-2:#4a4a4a;--ink-3:#7a7a7a;--ink-4:#a8a8a8;--paper:#fff;--card:#f3f3f2;--rule:#e5e7eb;--brand:#d97757;--page-w:210mm;--page-my:20mm;--page-mx:22mm}
  *{box-sizing:border-box;margin:0;padding:0}
  html,body{background:#e8e8e6;font-family:var(--font-sans);color:var(--ink);-webkit-font-smoothing:antialiased}
  .doc{padding:32px 0 64px;display:flex;flex-direction:column;align-items:center;gap:20px}
  .page{width:var(--page-w);min-height:297mm;background:var(--paper);padding:var(--page-my) var(--page-mx);box-shadow:0 1px 2px rgba(0,0,0,.04),0 8px 28px rgba(0,0,0,.07);display:flex;flex-direction:column;position:relative}
  .rh{display:flex;justify-content:space-between;align-items:center;padding-bottom:7mm;border-bottom:.75pt solid var(--rule);font-size:8pt;color:var(--ink-4);letter-spacing:.02em}
  .rh .brand{color:var(--ink-3);font-weight:500}
  .rh .brand b{color:var(--brand);font-weight:600}
  .rh .meta{font-family:var(--font-mono);font-size:7.5pt}
  .doc-label{margin-top:8mm;font-size:10pt;font-weight:600;color:var(--brand);letter-spacing:.14em;text-transform:uppercase}
  .doc-title{margin-top:3mm;font-size:19pt;font-weight:600;color:var(--ink);letter-spacing:-.01em;line-height:1.15}
  .destinatario{margin-top:9mm;padding:5mm 6mm;background:var(--card);border-radius:2px;display:grid;grid-template-columns:28mm 1fr;gap:1mm 5mm;align-items:baseline}
  .destinatario .d-label{font-size:8pt;font-weight:600;color:var(--ink-3);letter-spacing:.1em;text-transform:uppercase;padding-top:1mm}
  .destinatario .d-val{font-size:10.5pt;font-weight:600;color:var(--ink)}
  .destinatario .d-sub{font-size:9.5pt;color:var(--ink-2);font-weight:400}
  .body-text{margin-top:8mm;font-size:10.5pt;color:var(--ink-2);line-height:1.65;text-align:justify;text-wrap:pretty}
  .body-text strong{color:var(--ink);font-weight:600}
  .onus-label,.pedido-label{margin-top:8mm;font-size:10pt;font-weight:600;color:var(--brand);letter-spacing:.14em;text-transform:uppercase;padding-bottom:2mm;border-bottom:.75pt solid var(--rule)}
  .onus-card{margin-top:4mm;border:.75pt solid var(--rule);border-radius:2px}
  .onus-card-head{padding:4mm 6mm;background:var(--card);border-bottom:.75pt solid var(--rule);display:flex;align-items:center;gap:10px}
  .onus-card-head .act-num{font-family:var(--font-mono);font-size:9pt;color:var(--ink-3)}
  .onus-card-head .act-title{font-size:10.5pt;font-weight:600;color:var(--ink)}
  .onus-card-body{padding:5mm 6mm;display:grid;grid-template-columns:1fr 1fr;gap:3mm 8mm}
  .onus-row{display:flex;flex-direction:column;gap:1mm}
  .onus-row .k{font-size:8pt;font-weight:600;color:var(--ink-3);letter-spacing:.1em;text-transform:uppercase}
  .onus-row .v{font-size:10pt;color:var(--ink);line-height:1.4}
  .onus-row .v.mono{font-family:var(--font-mono);font-size:9.5pt;color:var(--ink-2)}
  .opcoes{margin-top:4mm;display:flex;flex-direction:column;gap:2mm}
  .opcao{display:grid;grid-template-columns:8mm 1fr;gap:3mm;align-items:baseline;padding:3.5mm 5mm;border:.75pt solid var(--rule);border-radius:2px;font-size:10pt;color:var(--ink-2);line-height:1.5}
  .opcao .letra{font-weight:600;color:var(--brand);font-family:var(--font-mono);font-size:9pt}
  .nota{margin-top:5mm;font-size:10pt;color:var(--ink-2);line-height:1.6;text-align:justify;text-wrap:pretty}
  .encerramento{margin-top:8mm;font-size:10pt;color:var(--ink-2);line-height:1.6}
  .local-data{margin-top:10mm;font-size:10pt;color:var(--ink-2)}
  .local-data strong{color:var(--ink)}
  .assinatura{margin-top:14mm;display:flex;flex-direction:column;align-items:flex-start;gap:2mm}
  .assinatura .linha{width:80mm;border-bottom:.75pt solid var(--ink);height:10mm}
  .assinatura .nome{font-size:11pt;font-weight:600;color:var(--ink)}
  .assinatura .cpf{font-family:var(--font-mono);font-size:9pt;color:var(--ink-3)}
  .rf{margin-top:auto;padding-top:7mm;border-top:.75pt solid var(--rule);display:flex;justify-content:space-between;font-family:var(--font-mono);font-size:7.5pt;color:var(--ink-4);letter-spacing:.02em}
  @page{size:A4;margin:0}
  @media print{
    html,body{background:#fff}
    .doc{padding:0;gap:0;display:block}
    .page{box-shadow:none;min-height:0;width:auto;display:block;padding:18mm 22mm;-webkit-box-decoration-break:clone;box-decoration-break:clone}
    .rf{display:none}
    .destinatario,.onus-card,.opcoes,.opcao,.assinatura{page-break-inside:avoid;break-inside:avoid}
    .doc-title,.onus-label,.pedido-label{page-break-after:avoid;break-after:avoid}
  }
</style>
</head>
<body>
<div class="doc"><div class="page">
  <header class="rh">
    <div class="brand"><img src="data:image/svg+xml;base64,${LOGO_RURALPLAN_B64}" alt="Ruralplan" style="height:4.5mm;width:auto;display:block" /></div>
    <div class="meta">${escapeHtml(cidadeUf)}, ${escapeHtml(dataExtenso)}</div>
  </header>
  <div class="doc-label">Requerimento</div>
  <h1 class="doc-title">Baixa de Ônus Real<br><span style="font-weight:400;color:var(--ink-3);font-size:13pt">(Hipoteca / Penhor Rural)</span></h1>
  <div class="destinatario">
    <div class="d-label">À</div>
    <div>
      <div class="d-val">${escapeHtml(credorTxt)}</div>
      <div class="d-sub">Assunto: Solicitação de baixa de ônus registrado em matrícula imobiliária</div>
      ${setorLine}
    </div>
  </div>
  <div class="body-text">
    Eu, <strong>${escapeHtml(nome)}</strong>,
    inscrito(a) no CPF nº <strong>${escapeHtml(cpf)}</strong>,
    na qualidade de proprietário(a) do imóvel rural objeto da
    Matrícula nº <strong>${escapeHtml(mat)}</strong>,
    do <strong>${escapeHtml(cartorio)}</strong>,
    venho, respeitosamente, à presença de V.Sa., expor e requerer o que segue:
  </div>
  <div class="body-text" style="margin-top:5mm">
    Conforme consta na referida matrícula, encontra(m)-se registrado(s) o(s) seguinte(s) ônus:
  </div>
  <div class="onus-label">Ônus registrados</div>
  ${cards}
  <div class="pedido-label">Pedido</div>
  <div class="body-text">Ocorre que a obrigação que deu origem ao referido ônus já foi integralmente quitada ou encontra-se vencida, não havendo, até o momento, baixa/cancelamento formal averbado na matrícula apresentada.</div>
  <div class="body-text" style="margin-top:4mm">Dessa forma, requer-se a emissão de documento formal de anuência/baixa, podendo ser:</div>
  <div class="opcoes">
    <div class="opcao"><span class="letra">a)</span><span>Termo de quitação com autorização expressa para cancelamento do ônus; ou</span></div>
    <div class="opcao"><span class="letra">b)</span><span>Carta de anuência dirigida ao Cartório de Registro de Imóveis competente; ou</span></div>
    <div class="opcao"><span class="letra">c)</span><span>Instrumento próprio exigido para averbação de baixa, conforme legislação vigente.</span></div>
  </div>
  <div class="nota">Solicita-se que o documento contenha identificação completa do credor, do devedor, do imóvel e do ato registral a ser cancelado, a fim de viabilizar a averbação junto ao Registro de Imóveis.</div>
  ${contatoLinha ? `<div class="nota" style="margin-top:4mm">${contatoLinha}</div>` : ''}
  <div class="nota" style="margin-top:4mm">Por fim, solicita-se a gentileza de encaminhamento do referido documento no prazo mais breve possível, preferencialmente em meio digital.</div>
  <div class="encerramento" style="margin-top:8mm">Sem mais para o momento, renovo votos de elevada consideração.</div>
  <div class="assinatura">
    <div class="linha"></div>
    <div class="nome">${escapeHtml(nome)}</div>
    <div class="cpf">CPF: ${escapeHtml(cpf)}</div>
  </div>
  <footer class="rf"><div>RURALPLAN · HUB DE REGULARIZAÇÃO FUNDIÁRIA</div><div>REQUERIMENTO DE BAIXA DE ÔNUS REAL</div></footer>
</div></div>
</body></html>`;
}

function resumoAtoRegistral(item: OnusItem): string {
  if (item.atos?.length) {
    return item.atos
      .map((a) => [a.descricao, a.data ? formatBR(String(a.data)) : ''].filter(Boolean).join(' · '))
      .join('; ');
  }
  return '';
}

function formatDataPtBR(d: Date): string {
  const meses = [
    'janeiro','fevereiro','março','abril','maio','junho',
    'julho','agosto','setembro','outubro','novembro','dezembro',
  ];
  return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
}

function sameCredor(a?: string, b?: string) {
  return normalizeCredor(a) === normalizeCredor(b);
}

function normalizeCredor(v?: string | null) {
  return (v || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function clean(v: unknown) {
  const s = String(v ?? '').trim();
  return s && s !== 'null' && s !== 'undefined' ? s : '';
}

function escapeHtml(v: string) {
  return v.replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[ch] || ch);
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      className="grid items-center"
      style={{ gridTemplateColumns: '140px 1fr', columnGap: '1rem', rowGap: 0 }}
    >
      <span className="cli-dt-label text-sm">{label}</span>
      <span className="text-sm min-w-0 break-words">{children}</span>
    </div>
  );
}

interface InlineEditProps {
  value: string | null | undefined;
  onCommit: (v: string | null) => Promise<void> | void;
  type?: 'text' | 'date' | 'select';
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  multiline?: boolean;
  displayHint?: string;
}

function InlineEdit({
  value,
  onCommit,
  type = 'text',
  options,
  placeholder,
  multiline = false,
  displayHint,
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value ?? '');
  const [feedback, setFeedback] = useState<'ok' | 'err' | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try {
        (inputRef.current as HTMLInputElement).select?.();
      } catch {
        /* noop */
      }
    }
  }, [editing]);

  function flash(kind: 'ok' | 'err') {
    setFeedback(kind);
    setTimeout(() => setFeedback(null), kind === 'ok' ? 800 : 1500);
  }

  async function commit() {
    setEditing(false);
    const original = value ?? '';
    if (draft === original) return;
    try {
      await onCommit(draft === '' ? null : draft);
      flash('ok');
    } catch (err) {
      console.error('inline edit erro:', err);
      flash('err');
    }
  }

  function cancel() {
    setDraft(value ?? '');
    setEditing(false);
  }

  function handleKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    } else if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      void commit();
    }
  }

  const bg =
    feedback === 'ok'
      ? 'rgba(5,150,105,.12)'
      : feedback === 'err'
        ? 'rgba(220,38,38,.12)'
        : undefined;

  if (!editing) {
    let display: ReactNode;
    if (type === 'date' && value) {
      display = formatBR(value);
    } else if (type === 'select' && value && options) {
      display = options.find((o) => o.value === value)?.label ?? value;
    } else if (value) {
      display = value;
    } else {
      display = <span className="cli-empty-muted">Pendente</span>;
    }
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setEditing(true);
          }
        }}
        title="Clique para editar"
        className="cursor-text inline-flex items-center h-[33px] w-full px-2 -mx-2 rounded transition-colors hover:bg-surface2"
        style={{ background: bg }}
      >
        <span className="text-text flex-1">{display}</span>
        {displayHint && <span className="ml-2 text-xs text-text-muted">{displayHint}</span>}
      </span>
    );
  }

  const fieldClass =
    'w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 text-sm text-text ' +
    'transition-colors focus:outline-none focus:border-[var(--accent)]';

  if (multiline) {
    return (
      <textarea
        ref={(el) => {
          inputRef.current = el;
        }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={handleKey}
        placeholder={placeholder}
        className={`${fieldClass} py-2 min-h-[80px] leading-snug`}
      />
    );
  }

  if (type === 'select' && options) {
    return (
      <select
        ref={(el) => {
          inputRef.current = el;
        }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={handleKey}
        className={`${fieldClass} h-[33px]`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      ref={(el) => {
        inputRef.current = el;
      }}
      type={type}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={handleKey}
      placeholder={placeholder}
      className={`${fieldClass} h-[33px]`}
    />
  );
}

function NewItemForm({
  item,
  onChange,
  onSave,
  onCancel,
}: {
  item: OnusItem;
  onChange: (i: OnusItem) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const fieldClass =
    'w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 text-sm text-text ' +
    'transition-colors focus:outline-none focus:border-[var(--accent)]';

  return (
    <div className="rounded-lg border border-[var(--accent)] bg-elevated p-4 flex flex-col gap-3">
      <div className="cli-section-label">Novo gravame</div>
      <div
        className="grid items-center"
        style={{ gridTemplateColumns: '140px 1fr 140px 1fr', columnGap: '1rem', rowGap: '0.5rem' }}
      >
        <span className="cli-dt-label text-sm">Credor</span>
        <input
          className={`${fieldClass} h-[33px]`}
          value={item.credor}
          onChange={(e) => onChange({ ...item, credor: e.target.value })}
          placeholder="Ex.: BASA"
          autoFocus
        />
        <span className="cli-dt-label text-sm">Tipo</span>
        <input
          className={`${fieldClass} h-[33px]`}
          value={item.tipo}
          onChange={(e) => onChange({ ...item, tipo: e.target.value })}
          placeholder="Ex.: Hipoteca"
        />
        <span className="cli-dt-label text-sm">Instrumento</span>
        <input
          className={`${fieldClass} h-[33px]`}
          value={item.instrumento}
          onChange={(e) => onChange({ ...item, instrumento: e.target.value })}
          placeholder="Ex.: Cédula 12/2024"
        />
        <span className="cli-dt-label text-sm">Ato registral</span>
        <input
          className={`${fieldClass} h-[33px]`}
          value={item.ato_registral}
          onChange={(e) => onChange({ ...item, ato_registral: e.target.value })}
          placeholder="Ex.: AV-2-16.708 / R-21-2.906"
        />
        <span className="cli-dt-label text-sm">Data do registro</span>
        <input
          type="date"
          className={`${fieldClass} h-[33px]`}
          value={item.data_registro ?? ''}
          onChange={(e) => onChange({ ...item, data_registro: e.target.value || null })}
        />
        <span className="cli-dt-label text-sm">Vencimento</span>
        <input
          type="date"
          className={`${fieldClass} h-[33px]`}
          value={item.vencimento ?? ''}
          onChange={(e) => onChange({ ...item, vencimento: e.target.value || null })}
        />
      </div>
      <div
        className="grid items-start"
        style={{ gridTemplateColumns: '140px 1fr', columnGap: '1rem' }}
      >
        <span className="cli-dt-label text-sm pt-2">Observações</span>
        <textarea
          className={`${fieldClass} py-2 min-h-[80px] leading-snug`}
          value={item.observacoes}
          onChange={(e) => onChange({ ...item, observacoes: e.target.value })}
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={onSave}>
          Salvar
        </Button>
      </div>
    </div>
  );
}

function formatBR(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}
